"use client";

import type { ComponentProps } from "react";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { debounce } from "@/ui/hooks/use-debounced-value";
import { Label } from "@/ui/shadcn/label";
import { StatefulInput } from "@/ui/stateful-input";

type BuilderInputFieldProps = {
	isTheme?: boolean;
	label: string;
	required?: boolean;
	placeholder?: string;
	description?: string;
	debounceMs?: number;
	onValueChange?: (value: string) => void;
} & Omit<ComponentProps<typeof StatefulInput>, "onValueChange" | "defaultValue"> &
	({ fieldPath: string } | { fieldPath?: never; onValueChange: (value: string) => void }) &
	(
		| { defaultValue: string | undefined | null; value?: never }
		| { defaultValue?: never; value: string | undefined | null }
	);

export const BuilderInputField = ({
	isTheme = false,
	label,
	required = false,
	placeholder,
	description,
	debounceMs = 300,
	fieldPath,
	onValueChange,
	defaultValue,
	value,
	className = "",
	...props
}: BuilderInputFieldProps) => {
	const context = useStoreBuilderContext();
	const saveMethod = isTheme ? "saveSelectedPageSectionThemeField" : "saveSelectedPageSectionField";

	const baseHandler = (newValue: string) => {
		if (onValueChange) {
			onValueChange(newValue);
		} else if (fieldPath) {
			context[saveMethod]({ fieldPath, value: newValue });
		}
	};

	const handleValueChange =
		debounceMs > 0 ? debounce(baseHandler, debounceMs, { trailing: true }) : baseHandler;

	return (
		<Label className="grid gap-1">
			<span className="text-xs text-neutral-600">{label}</span>
			<StatefulInput
				required={required}
				placeholder={placeholder}
				defaultValue={defaultValue || undefined}
				value={value || undefined}
				onValueChange={handleValueChange}
				className={className}
				{...props}
			/>
			{description && <p className="text-[0.8rem] text-muted-foreground">{description}</p>}
		</Label>
	);
};
